import { FC, PropsWithChildren } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/hooks/auth';
import { AUTH_REDIRECT_KEY } from '@/utils/constants';

import LoginGate from './LoginGate';

/**
 * Gate for pages that require an account. Renders INSIDE SecondRootLayout, so
 * the header, the session check and the footer are always present — only the
 * page CONTENT is gated.
 *
 * The protected `children` render ONLY when the visitor is confirmed logged in.
 * They are never rendered while the auth check is in flight, and never for a
 * logged-out visitor — which is both why the page no longer flashes before a
 * redirect, and the security boundary: an unauthenticated visitor's page
 * component and its data fetches never mount, so there is nothing to reveal by
 * removing an overlay in devtools. (The API is the second boundary: every
 * protected request 401s without the token.)
 *
 * A logged-out visitor stays on the page's URL, sees the header and a neutral
 * backdrop where the page would be, and the login modal over it. On login the
 * flow reloads that same URL, now authenticated, and the page mounts. The ✕
 * navigates home.
 */
const AuthGuard: FC<PropsWithChildren> = ({ children }) => {
  const { isLoggedIn, isLoading } = useAuth();
  const location = useLocation();

  if (isLoggedIn) return <>{children}</>;

  // A flow that revoked its own session can leave a specific destination — an
  // explanation page (e.g. "your account is being deleted"), not the generic
  // login gate. Honour it, read-and-remove so it can't outlive this redirect.
  if (!isLoading) {
    try {
      const stashed = sessionStorage.getItem(AUTH_REDIRECT_KEY);
      if (stashed) {
        sessionStorage.removeItem(AUTH_REDIRECT_KEY);
        return <Navigate to={stashed} state={{ from: location }} />;
      }
    } catch {
      /* sessionStorage blocked — fall through to the gate */
    }
  }

  // Logged out, or still checking: the page never mounts. Only the backdrop,
  // with the login modal once the auth check has settled.
  return <LoginGate showModal={!isLoading} />;
};

export default AuthGuard;
